/**
 * Step-up replay queue — shared state between the StepUpInterceptor (which
 * detects STEP_UP_REQUIRED mutation failures) and the StepUpModalHost (which
 * runs the MFA challenge and replays the failed mutations).
 *
 * FF-UI-2: the interceptor used to full-page-navigate to /auth/refresh-step-up,
 * destroying whatever modal/wizard the user was in and silently dropping the
 * mutation. Now the failed TanStack `Mutation` objects queue here, a modal
 * overlay completes step-up IN PLACE, and each queued mutation is re-executed
 * with its original variables — the underlying screen never unmounts.
 *
 * Why zustand (not React context): the interceptor enqueues from a mutation-
 * cache subscription (outside React's render cycle) via getState(), and the
 * host subscribes with a selector. No provider plumbing needed.
 */

import { create } from 'zustand';
import type { Mutation } from '@tanstack/react-query';

/** The cache-level mutation object (not the hook result). `execute()` re-runs
 *  the mutationFn with the supplied variables and dispatches through the same
 *  state machine, so the owning component's `isPending`/`onSuccess` all fire
 *  exactly as if the original call had succeeded. */
export type AnyMutation = Mutation<unknown, unknown, unknown, unknown>;

interface StepUpQueueState {
  /** True while the step-up modal should be visible. */
  open: boolean;
  /** Failed mutations awaiting replay, oldest first. */
  pending: AnyMutation[];
  /** Add a failed mutation (deduped by mutationId) and open the modal. */
  enqueue: (m: AnyMutation) => void;
  /** Take the queue for replay and close the modal. */
  drain: () => AnyMutation[];
  /** User cancelled — empty the queue and close. The mutations stay in their
   *  error state, which the owning surfaces already render. */
  dismiss: () => void;
}

export const useStepUpQueue = create<StepUpQueueState>((set, get) => ({
  open: false,
  pending: [],
  enqueue: (m) => {
    const { pending } = get();
    if (pending.some((p) => p.mutationId === m.mutationId)) return;
    set({ pending: [...pending, m], open: true });
  },
  drain: () => {
    const out = get().pending;
    set({ pending: [], open: false });
    return out;
  },
  dismiss: () => set({ pending: [], open: false }),
}));

/**
 * Replay-loop guard. A mutation that fails STEP_UP_REQUIRED *again* after a
 * replay (clock skew, a second 600s expiry mid-flight) must NOT re-open the
 * modal — it falls through to the surface's normal error UI instead. WeakSet
 * so completed mutations garbage-collect with the cache.
 */
const replayedOnce = new WeakSet<AnyMutation>();

export function markReplayed(m: AnyMutation): void {
  replayedOnce.add(m);
}

export function wasReplayed(m: AnyMutation): boolean {
  return replayedOnce.has(m);
}
